import { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import styles from './manage.module.css';
import { Flex, Box, Text, Title, useMantineTheme, Button, Badge, CopyButton, Tooltip, ActionIcon, Group, PasswordInput, Loader } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router'
import SettingsMenu from '@/components/settings/menu';
import { useLocation } from '@tanstack/react-router';
import { IconCheck, IconCopy, IconExternalLink, IconTrash, IconEye, IconEyeOff, IconPlus } from '@tabler/icons-react';
import moment from 'moment';
import SCModal from '@/PageComponents/Modal/SCModal';
import integrationService from '@/services/integration-service';
import ToastContext from '@/utils/toast-context';
import permissionService from '@/services/permission/permission-service';

interface GuideItem { title: string, description: string }
interface ApiKey {
    key: string,
    id: string,
    isActive: boolean,
    createdDate: Date
}

const quickGuides: GuideItem[] = [
    {
        title: 'Generate key',
        description: 'Create a unique API key for your environment in the management section.'
    },
    {
        title: 'Authorize',
        description: 'Include the key in your HTTP request headers as X-API-KEY.'
    },
    {
        title: 'First request',
        description: 'Ping the status endpoint to verify your connection.'
    },
]

const ApiKeyManage = () => {
    const theme = useMantineTheme();
    const location = useLocation();
    const toast = useContext(ToastContext);
    const [showModal, setShowModal] = useState(false);
    const [currentApiKey, setCurrentApiKey] = useState<ApiKey | undefined>();
    const [loading, setLoading] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const isServCraftAdmin = permissionService.isMasterSystemAdmin();
    const showButtonIcon = useMediaQuery('(min-width: 1000px)');

    let currPath = "";
    if (location)
        currPath = location.pathname;

    useEffect(() => {
        const load = async () => {
            const result = await integrationService.getExternalApiKey();
            if (result?.ID) {
                setCurrentApiKey({
                    key: result.AccessToken,
                    id: result.ID,
                    isActive: result.IsActive,
                    createdDate: new Date(result.CreatedDate),
                });
            }
        };
        load();
    }, []);

    const createApiKey = async () => {
        if (currentApiKey || loading) return;
        setLoading(true);
        const result = await integrationService.createExternalApiKey();
        if (result?.ID) {
            setCurrentApiKey({
                key: result.AccessToken,
                id: result.ID,
                isActive: result.IsActive,
                createdDate: new Date(result.CreatedDate),
            });
        } else {
            toast.setToast({ message: result?.message || 'Failed to create API key', show: true, type: 'error' });
        }
        setLoading(false);
    };

    const deactivateApiKey = async () => {
        if (!currentApiKey || loading) return;
        setLoading(true);
        const result = await integrationService.updateExternalApiKey(currentApiKey.id, false);
        if (result?.ID) {
            setCurrentApiKey(undefined);
            setShowModal(false);
        } else {
            toast.setToast({ message: result?.message || 'Failed to deactivate API key', show: true, type: 'error' });
        }
        setLoading(false);
    };

    const quickGuideCard = (guideItem: GuideItem, guideItemNumber: number) => (<Flex>
        <Flex w={30} h={27} direction="column" justify="center" align="center" bg={'scBlue'}
            style={{ borderRadius: 5, flexShrink: 0 }} mr={'md'} c={'white'}
        >
            {guideItemNumber}
        </Flex>
        <Box>
            <Text fw={700} mb={5} size={'md'} >{guideItem.title}</Text>
            <Text size={'sm'}>{guideItem.description}</Text>
        </Box>
    </Flex>)

    return (<>
        <div className="settings-container">
            <Flex>
                <SettingsMenu urlPath={currPath} />
                <Box className="settings-card">
                    <Flex wrap='wrap' px={'lg'} py={'xl'} align={'flex-start'}>
                        <Box w={'100%'} >
                            <Title mb={'xs'} order={4}>API Access</Title>
                            <Text size={'sm'} w={{base:'100%',sm:'80%', lg:'70%' ,xl:'40%'}} maw={{lg:470}}>
                                The ServCraft API lets you extend ServCraft beyond its built-in features.
                                Build advanced reporting, pull data into other platforms, automate workflows based on job changes, and connect ServCraft to your other business tools.
                                Your business can have one API Key at a time. Anyone with the Integrations permission will have access to the API Key.
                                Note: Jobs cannot be created through the API.
                            </Text>
                                <Flex align={'center'} gap={4}>
                                <Text size="sm" c={'#2B2B2B'}>Read the documentation to learn more: </Text> 
                                <Text c={'scBlue'} fw={700} size={'sm'}>
                            <a href="https://developers.servcraft.co.za/swagger/index.html" target="_blank" rel="noopener noreferrer" className={styles.swaggerLink}>ServCraft API Documentation
                            <IconExternalLink size={'16px'} strokeWidth={2} color={theme.colors.scBlue[10]} style={{ marginLeft: 4, verticalAlign: 'text-bottom', position: 'relative', top: -1 }} /></a>
                            </Text>
                            </Flex>
                            </Box>
                        <Flex mt={'50'} justify={'space-between'} gap={'xl'} wrap={'wrap'}>
                            <Flex className={styles.cardContainer} maw={250} px={'xl'} py={'lg'} gap={'md'} direction="column">
                                <Title order={4}>Quick start guide</Title>
                                {quickGuides.map((guideItem, index) => quickGuideCard(guideItem, index + 1))}
                            </Flex>
                            <Flex className={styles.cardContainer} px={'xl'} py={'lg'} gap={'md'} miw={460} direction="column" style={{ alignSelf: 'flex-start' }}>
                                <Flex align={'center'} justify={'space-between'}>
                                    <Box>
                                        <Title order={4}>API Key</Title>
                                    </Box>
                                    <Button
                                        disabled={!!currentApiKey || loading || isServCraftAdmin}
                                        onClick={createApiKey}
                                        ml={'lg'}
                                        rightSection={loading && !currentApiKey ? <Loader variant={'oval'} size={18} color={'white'} /> : showButtonIcon ? <IconPlus size={20} color={'white'} /> : undefined}
                                    >Create Key</Button>
                                </Flex>
                                {currentApiKey ?
                                    <>
                                        <PasswordInput value={currentApiKey.key} visible={showApiKey} rightSection={
                                            <Group gap={4} wrap="nowrap" style={{ paddingRight: 40 }}>
                                                <CopyButton value={currentApiKey.key} timeout={2000}>
                                                    {({ copied, copy }) => (
                                                        <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                                                            <ActionIcon variant="subtle" color={'#2B2B2B'} onClick={copy}>
                                                                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )}
                                                </CopyButton>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="#2B2B2B"
                                                    onClick={() => setShowApiKey((v) => !v)}
                                                    size="sm"
                                                >
                                                    {showApiKey ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                                </ActionIcon>
                                            </Group>} />
                                        <Flex my={'sm'} justify={'space-between'} >
                                            <Text size={'sm'}>Created {moment(currentApiKey.createdDate).format('DD MMMM YYYY')}</Text>
                                            <IconTrash color={'#FC2E50'} onClick={() => setShowModal(true)} stroke={2} />
                                        </Flex>
                                    </>
                                    :
                                    <Flex justify='center' wrap={'wrap'} align='center' direction={'column'} mih={160}>
                                        <Title order={5}>No API keys yet</Title>
                                        <Text>Create your API key to use the ServCraft API</Text>
                                    </Flex>
                                }
                            </Flex>
                        </Flex>
                    </Flex>
                </Box>
            </Flex>
        </div>
        <SCModal open={showModal} onClose={() => setShowModal(false)} size={550}>
            <Title order={5}>Deactivate API key?</Title>
            <Box mt={'lg'}>Deactivating this key will revoke access for any applications or services using it.</Box>
            <Flex mt={'xl'} justify={'flex-end'} gap={'md'}>
                <Button variant={'outline'} style={{ borderColor: '#D8D8D8' }} onClick={() => setShowModal(false)} c={'#525866'} disabled={loading}>Cancel</Button>
                <Button variant={'outline'} onClick={deactivateApiKey} style={{ borderColor: '#FC2E50' }} c={'#FC2E50'} disabled={isServCraftAdmin || loading} rightSection={loading && <Loader variant={'oval'} size={18} color={'#FC2E50'} />}>Deactivate</Button>
            </Flex>
        </SCModal>
    </>)
}

export const Route = createFileRoute('/_authenticated/settings/api/manage')({ component: ApiKeyManage })
export default ApiKeyManage;
